import React, { createContext, FC, ReactElement, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { lazy } from 'yup';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import Details from 'buyside/components/presentation/IdeaForm/Details/details';
import Disclaimers from 'buyside/components/presentation/IdeaForm/Disclaimers/disclaimers';
import Positions from 'buyside/components/presentation/IdeaForm/Positions/positions';
import ResearchFormWizard, {
  ResearchWizardStep
} from 'buyside/components/presentation/ResearchFormWizard/researchFormWizard';
import WriteupAndAttachments from 'buyside/components/presentation/IdeaForm/WriteupAndAttachments/writeupAndAttachments';

import {
  determineCompletedSteps,
  generateDefaultValues,
  generateValidationSchema,
  IdeaFormInput
} from 'buyside/util/researchSubmission';

import { IdeaSubmissionQuery, useCreateOrUpdateIdeaMutation } from 'interfaces/graphql-typings';

export const IdeaFormContext = createContext<{
  currentContests?: IdeaSubmissionQuery['currentContests'];
  currentUser?: IdeaSubmissionQuery['currentUser'];
  ideaId?: string;
  isDraft: boolean;
  termsOfUse?: IdeaSubmissionQuery['termsOfUse'];
}>({ isDraft: true });

const steps = [
  {
    label: 'Position & Details',
    name: 'position_and_details',
    component: (): ReactElement => (
      <>
        <Positions />
        <Details />
      </>
    )
  },
  {
    label: 'Writeup & Attachments',
    name: 'writeup_and_attachments',
    component: WriteupAndAttachments
  },
  {
    label: 'Review & Submit',
    name: 'review_and_submit',
    component: Disclaimers
  }
];

const IdeaForm: FC<{
  data?: IdeaSubmissionQuery;
  id?: string;
}> = ({ data, id }) => {
  const navigate = useNavigate();

  const { currentContests, currentUser, idea, termsOfUse } = data || {};
  const { id: currentUserId, isAdmin } = currentUser || {};
  const { status, user: ideaUser } = idea || {};

  const values = useMemo(() => generateDefaultValues(idea), [idea]);

  const completedStepNames = useMemo(() => (values ? determineCompletedSteps(values) : []), [
    values
  ]);

  const initialStep = useMemo(
    () => steps.find(step => !completedStepNames.includes(step.name)) || steps[steps.length - 1],
    [completedStepNames]
  );

  const [currentStep, setCurrentStep] = useState<ResearchWizardStep>(id ? initialStep : steps[0]);

  const methods = useForm<IdeaFormInput>({
    resolver: yupResolver(
      lazy(values =>
        generateValidationSchema({
          currentStepName: currentStep.name,
          isPairTrade: (values as IdeaFormInput).positions.length === 2
        })
      )
    ),
    values
  });

  const handleSetCurrentStep = useCallback((step: ResearchWizardStep) => {
    window.scrollTo(0, 0);
    return setCurrentStep(step);
  }, []);

  const [handleCreateOrUpdateIdea] = useCreateOrUpdateIdeaMutation();

  const completedSteps = useMemo(
    () => (completedStepNames ? steps.filter(step => completedStepNames.includes(step.name)) : []),
    [completedStepNames]
  );

  const handleCreateOrUpdateDraft = useCallback(
    (values: IdeaFormInput, nextStep: ResearchWizardStep, isSubmission: boolean) => {
      const variables = {
        ...values,
        attachments: values.attachments.map(
          ({ id, attachmentType, file, filename, researchType }) => ({
            id,
            attachmentType,
            file,
            filename,
            researchType
          })
        ),
        currentStep: currentStep.name,
        isSubmission,
        mentionedEntities: values.mentionedEntities.map(({ value }) => value)
      };

      return handleCreateOrUpdateIdea({ variables })
        .then(response => {
          handleSetCurrentStep(nextStep);
          const idea = response.data?.createOrUpdateIdea?.idea;
          if (!id && idea?.id) navigate(idea.id, { replace: true });
        })
        .catch(error => toast.error(error.message, { autoClose: 7500, theme: 'colored' }));
    },
    [currentStep.name, handleCreateOrUpdateIdea, handleSetCurrentStep, navigate, id]
  );

  if (ideaUser && ideaUser.id !== currentUserId && !isAdmin)
    return (
      <Navigate
        to="/"
        state={{
          cannotEditError: `You cannot edit idea with ID ${id}. Contact support@sumzero.com if you believe this is an error.`
        }}
      />
    );

  const isDraft = status === 'draft' || !idea;

  return (
    <IdeaFormContext.Provider
      value={{
        currentContests,
        currentUser,
        ideaId: id,
        isDraft,
        termsOfUse
      }}
    >
      <FormProvider {...methods}>
        <ResearchFormWizard
          completedSteps={completedSteps}
          currentStep={currentStep}
          handleCreateOrUpdateDraft={handleCreateOrUpdateDraft}
          handleSetCurrentStep={handleSetCurrentStep}
          isDraft={isDraft}
          researchPath={`/pro/research/ideas/${id}`}
          researchType="Idea"
          steps={steps}
        />
      </FormProvider>
    </IdeaFormContext.Provider>
  );
};

export default IdeaForm;
